/*
 * CustomerInvoiceForm.tsx : Un formulaire pour ajouter ou modifier une facture client.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import {
	faFileInvoiceDollar,
	faClock,
	faInfoCircle,
	faClipboardList,
	faChartLine,
	faMoneyBills,
	faMoneyBillTransfer,
} from '@fortawesome/free-solid-svg-icons';

// Import Communs et Layout
import {
	DatePickerField,
	finishFormSave,
	FooterTotal,
	FormSaveActions,
	InputField,
	SelectField,
	PdfCard,
	RelatedItemsManager,
	StatusCard,
	TextAreaField,
	asLocalDateString,
	parseLocalDate,
	useFormDirty,
} from '../../../common';
import { useWorkflowStatusConfig } from '../../../common/hooks/useWorkflowStatusConfig';
import WorkflowLockBanner from '../../../common/components/WorkflowLockBanner/WorkflowLockBanner';
import CardHeader from '../../../layout/CardHeader';
import CardSection from '../../../layout/CardSection';

// Import CustomerInvoice
import {
	CUSTOMER_INVOICE_STATUS_CONFIG,
	CustomerInvoiceData,
} from '../types/types';
import { customerInvoicesApi } from '../services/customerInvoicesApi';

// Import Affaires
import { projectsApi } from 'features/projects/services/projectsApi';

// Imports Éléments logistiques
import { LogisticElementData } from 'features/logisticElements/types/types';
import { logisticElementsApi } from 'features/logisticElements/services/logisticElementsApi';

// Imports Activités
import { ActivityData } from 'features/activities/types/types';
import { activitiesApi } from 'features/activities/services/activitiesApi';

// Imports Acomptes
import { AdvanceData, advancesApi } from 'features/advances';

// Imports Avoirs
import { CreditNoteData } from 'features/creditNotes/types/types';
import { creditNotesApi } from 'features/creditNotes/services/creditNotesApi';

function editableInvoice(invoice: CustomerInvoiceData) {
	const editable = { ...invoice };
	delete editable.status;
	delete editable.workflowLock;
	return editable;
}

// Composant CustomerInvoiceForm
const CustomerInvoiceForm: React.FC = () => {
	// Hooks de Chargement
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);

	// Hook utilisateur courant
	const userId = localStorage.getItem('userId');
	const userName = localStorage.getItem('name');
	const userFirstName = localStorage.getItem('firstName');

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const statusConfig = useWorkflowStatusConfig(
		'customer_invoice',
		CUSTOMER_INVOICE_STATUS_CONFIG
	);

	// Hook de gestion du formulaire
	const [formData, setFormData] = React.useState<CustomerInvoiceData>({
		id: 0,
		referenceNumber: '',
		invoiceDate: new Date(),
		dueDate: (() => {
			const due = new Date();
			due.setDate(due.getDate() + 60);
			return due;
		})(),
		fiscalYear: new Date().getFullYear(),
		label: '',
		acceptanceReport: '',
		status: 'DRAFT',
		project: null,
		activities: [],
		logisticElements: [],
		createdBy: userId
			? {
					id: Number(userId),
					name: userName || '',
					firstName: userFirstName || '',
				}
			: undefined,
		createdAt: new Date()
			.toISOString()
			.replace('Z', '+00:00') as unknown as Date,
	});
	// L'état et le verrou sont enregistrés dès le clic sur le bloc État.
	const { isDirty, markClean } = useFormDirty(
		editableInvoice(formData),
		baselineReady
	);

	// Hook pour les options de sélection des affaires (projects)
	const [projectOptions, setProjectOptions] = React.useState<
		{ key: string; label: string }[]
	>([]);
	const [loadingProjects, setLoadingProjects] = React.useState(true);

	// Hook pour le token d'authentification
	const token = localStorage.getItem('token') || '';

	// Hook pour le prochain numéro de référence
	const [nextRef, setNextRef] = React.useState<string | null>(null);

	// Hook pour les dates de filtrage des activités
	const [activityDateFilter, setActivityDateFilter] = React.useState<{
		beginDateMin?: string;
		beginDateMax?: string;
	}>({});

	// Hook pour savoir si l'utilisateur a modifié dueDate
	const [isDueDateManual, setIsDueDateManual] = React.useState(false);

	// Loading : Fetch des données et remplissage du formulaire (une fois le composant monté, si un id est présent)
	React.useEffect(() => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		if (id) {
			customerInvoicesApi
				.get(Number(id), token)
				.then((data) => {
					setFormData(data);
					if (data.dueDate) setIsDueDateManual(true);
					// Préremplir les selects
					if (data.project) {
						setProjectOptions([
							{
								key: data.project.id.toString(),
								label: data.project.referenceNumber,
							},
						]);
					}
				})
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
		}
	}, [id]);

	// Lazy loading : options du select Affaires (projects) — route /select allégée
	React.useEffect(() => {
		const token = localStorage.getItem('token');
		if (!token) return;

		if (!loading && loadingProjects) {
			projectsApi
				.listSelect(token)
				.then((items) => {
					setProjectOptions(
						items.map((item) => ({
							key: item.id.toString(),
							label: String(item.referenceNumber),
						}))
					);
				})
				.catch(console.error)
				.finally(() => setLoadingProjects(false));
		}
	}, [loading, loadingProjects]);

	// Lazy load du prochain numéro de référence
	React.useEffect(() => {
		// Vérification de la création d'une nouvelle commande
		if (id) return;

		// Vérification du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;

		// Si on n'a pas déjà récupéré la prochaine référence
		if (!loading && nextRef === null) {
			// Récupération de l'année en cours
			const year = new Date().getFullYear();

			// Fetch du prochain numéro de référence
			customerInvoicesApi
				.previewReference(token, year)
				.then((data) => {
					setNextRef(data.nextReference);
					// Mise à jour du numéro de référence dans le formulaire
					setFormData((prevData) => ({
						...prevData,
						referenceNumber: 'FC' + data.nextReference,
					}));
					setBaselineReady(true);
				})
				.catch((error) => {
					console.error(error);
					setBaselineReady(true);
				});
		}
	}, [loading]);

	// Handle Formulaire : Soumission
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		// Vérifier la présence du token d'authentification
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const dataToSubmit = {
			...formData,
			invoiceDate: asLocalDateString(formData.invoiceDate) as unknown as Date,
			dueDate: asLocalDateString(formData.dueDate) as unknown as Date,
		};
		const onLeave = () => navigate('/app/customerInvoices');
		// Si un id est présent, mise à jour de l'élément existant
		if (id) {
			customerInvoicesApi
				.update(Number(id), dataToSubmit, token)
				.then(() =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean: () => markClean(editableInvoice(formData)),
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			// Sinon, création d'un nouvel élément
			customerInvoicesApi
				.create(dataToSubmit, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/customerInvoices/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		// Empêcher le comportement par défaut du formulaire
		e.preventDefault();
		handleSave(true);
	};

	// handle Formulaire : Changement d'état
	const handleStatusChange = (newStatus: string) => {
		if (formData) {
			const token = localStorage.getItem('token');
			if (!token) return;
			customerInvoicesApi
				.update(formData.id, { status: newStatus }, token)
				.then((updated) => {
					setFormData((prev) =>
						prev
							? {
									...prev,
									status: updated?.status ?? newStatus,
									workflowLock: updated?.workflowLock ?? null,
								}
							: prev
					);
				})
				.catch((error) => console.error(error));
		}
	};

	// Handle Formulaire : Changements des inputs
	const handleChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Handle Formulaire : Changements des selects
	const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		if (name === 'project') {
			const selectedOption = projectOptions.find((opt) => opt.key === value);
			if (selectedOption) {
				setFormData((prev) => ({
					...prev,
					project: {
						id: Number(selectedOption.key),
						referenceNumber: selectedOption.label,
					},
				}));
			}
			return;
		}

		// Mise à jour standard pour les autres champs
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	// Fonction de vérification des pre requis pour la génération du pdf de la facture client et afficher une alerte si des éléments sont manquants
	const canGeneratePdf = () => {
		// Projet associé
		if (!formData.project) {
			alert('Veuillez sélectionner un projet.');
			return false;
		}
		// Numéro de référence
		if (!formData.referenceNumber) {
			alert('Veuillez saisir un numéro de référence.');
			return false;
		}
		// Libellé
		if (!formData.label) {
			alert('Veuillez saisir un libellé.');
			return false;
		}
		// Adresse de type facturation (FAC)
		if (
			!formData.project.quote?.tiers?.addresses?.some((addr) =>
				addr.types.some((type) => type.code === 'FAC')
			)
		) {
			alert(
				'Le client associé au projet doit avoir une adresse de facturation.'
			);
			return false;
		}
		// Adresse de type principale (PRI)
		if (
			!formData.project.quote?.tiers?.addresses?.some((addr) =>
				addr.types.some((type) => type.code === 'PRI')
			)
		) {
			alert('Le client associé au projet doit avoir une adresse principale.');
			return false;
		}
		return true;
	};

	// Rendu du composant, le premier div est la card principale
	return (
		<div className="max-w-6xl mx-auto p-6 bg-white rounded-lg shadow-md space-y-6">
			{/* HEADER DE LA CARD */}
			<CardHeader
				icon={faFileInvoiceDollar}
				title={id ? "Édition d'une facture client" : 'Nouvelle facture client'}
				createdAt={
					formData.createdAt ? formData.createdAt?.toLocaleString() : undefined
				}
				createdBy={
					formData.createdBy
						? formData.createdBy.firstName + ' ' + formData.createdBy.name
						: undefined
				}
				loading={loading}
			/>

			{/* CONTENU DE LA CARD */}
			<WorkflowLockBanner lock={formData.workflowLock} />
			<form onSubmit={handleSubmit} className="space-y-6">
				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* COLONNE GAUCHE */}
					<CardSection icon={faInfoCircle} title="Informations générales">
						<fieldset
							disabled={Boolean(formData.workflowLock?.locked)}
							className="m-0 min-w-0 space-y-4 border-0 p-0"
						>
							<SelectField
								label="Affaire"
								selectedKey={formData.project?.id?.toString() || ''}
								onValueChange={(val) =>
									handleChangeSelect({
										target: {
											name: 'project',
											value: val,
										},
									} as React.ChangeEvent<HTMLSelectElement>)
								}
								options={projectOptions}
								loading={loading}
								optionsLoading={loadingProjects}
								entityHref={
									formData.project?.id
										? `/app/projects/${formData.project.id}/edit`
										: undefined
								}
								onClear={() =>
									setFormData((prev) => ({
										...prev,
										project: null,
									}))
								}
							/>
							<InputField
								label="Numéro de référence"
								name="referenceNumber"
								value={formData.referenceNumber}
								onChange={handleChange}
								loading={loading}
								placeholder="Chargement..."
								readOnly={id ? false : true}
							/>
							<InputField
								label="Libellé"
								name="label"
								value={formData.label}
								onChange={handleChange}
								loading={loading}
								placeholder='ex: "Affaire Tartempion - Devis M25001Q"'
							/>
							<InputField
								label="Exercice"
								name="fiscalYear"
								type="number"
								value={formData.fiscalYear}
								onChange={handleChange}
								loading={loading}
								placeholder="ex: 2025"
							/>
							<TextAreaField
								label="PV de réception"
								name="acceptanceReport"
								value={formData.acceptanceReport}
								onChange={handleChange}
								loading={loading}
								placeholder='ex: "PV reçu le 15/03/2025"'
							/>
							<InputField
								label="Statut"
								name="status"
								value={formData.status}
								onChange={handleChange}
								loading={loading}
								placeholder='saisir "DRAFT" pour initialiser un état'
								visibility={true}
							/>
						</fieldset>
					</CardSection>

					{/* COLONNE DROITE */}
					<div className="flex flex-col gap-6">
						{/* CARD Contenant le statut de la facture */}
						<StatusCard
							status={formData?.status}
							config={statusConfig}
							onStatusChange={handleStatusChange}
							size="md"
							loading={loading}
						/>

						<PdfCard
							resource="customer-invoices"
							entity={formData}
							onEntityUpdate={(updated) => setFormData(updated)}
							checkCanGenerate={canGeneratePdf}
							loading={loading}
							size="md"
							className="w-full"
						/>

						{/* Dates et périodicité */}
						<CardSection icon={faClock} title="Dates et périodicité">
							<DatePickerField
								label="Date de la facture"
								name="invoiceDate"
								value={parseLocalDate(formData.invoiceDate)}
								onChange={(date) =>
									setFormData((prev) => {
										const next = { ...prev, invoiceDate: date };
										if (!isDueDateManual && date) {
											const due = new Date(date);
											due.setDate(due.getDate() + 60);
											next.dueDate = due;
										}
										return next;
									})
								}
								loading={loading}
							/>

							<DatePickerField
								label="Date d'échéance"
								name="dueDate"
								value={parseLocalDate(formData.dueDate)}
								onChange={(date) => {
									setFormData((prev) => ({ ...prev, dueDate: date }));
									setIsDueDateManual(true);
								}}
								loading={loading}
							/>
						</CardSection>
					</div>
				</div>

				{/* 1 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-1 gap-6">
					{/* TABLEAU DES ACTIVITÉS À FACTURER */}
					<CardSection icon={faChartLine} title="Activités">
						<RelatedItemsManager<ActivityData>
							key={`${activityDateFilter.beginDateMin || ''}-${activityDateFilter.beginDateMax || ''}-${formData.project?.id || ''}`}
							linkedItems={formData.activities || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, activities: items }))
							}
							fetchAll={() => {
								const token = localStorage.getItem('token');
								if (!token) {
									// Pas de token : retourner une liste vide pour éviter l'assertion non-null
									return Promise.resolve([]);
								}
								// Préparer les filtres
								const filters: Record<string, string> = {};
								if (formData.project?.id)
									filters['project.id'] = formData.project.id.toString();
								if (activityDateFilter.beginDateMin)
									filters['beginDate_from'] = activityDateFilter.beginDateMin;
								if (activityDateFilter.beginDateMax) {
									filters['beginDate_to'] = activityDateFilter.beginDateMax;
								}
								return activitiesApi
									.listPaginated(token, { page: 1, limit: 5000, filters })
									.then((res) => ('data' in res ? res.data : res));
							}}
							isLinked={(a) => !!a.customerInvoice?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'beginDate', label: 'Date' },
								{ key: 'origin', label: 'Origine' },
								{ key: 'service', label: 'Service' },
								{
									key: 'durationHours',
									label: 'Durée',
									render: (_value: unknown, item: ActivityData) => {
										if (item.durationHours == null) return '—';
										const totalMinutes = Math.round(item.durationHours * 60);
										const hours = Math.floor(totalMinutes / 60);
										const minutes = totalMinutes % 60;
										return minutes > 0
											? `${hours}h${String(minutes).padStart(2, '0')}`
											: `${hours}h`;
									},
								},
								{
									key: 'unitPriceExclTax',
									label: 'Tarif HT',
									render: (_value: unknown, item: ActivityData) =>
										item.unitPriceExclTax != null
											? new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(Number(item.unitPriceExclTax))
											: '—',
								},
								{
									key: 'project',
									label: 'Affaire',
									render: (_value: unknown, item: ActivityData) => {
										if (!item.project?.id) return '—';
										return (
											<a
												href={`/app/projects/${item.project.id}/edit`}
												className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
												onClick={(e) => e.stopPropagation()}
											>
												{item.project.referenceNumber ?? `#${item.project.id}`}
											</a>
										);
									},
								},
								{
									key: 'badgeMovements',
									label: 'Propriétaire',
								},
							]}
							linkedTitle="Liées"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/activities"
							linkedFooterTotals={(() => {
								const activities = formData.activities || [];
								// Total des heures
								const totalHoursMinutes = Math.round(
									activities.reduce(
										(sum, a) => sum + (a.durationHours ?? 0),
										0
									) * 60
								);
								const h = Math.floor(totalHoursMinutes / 60);
								const m = totalHoursMinutes % 60;
								const totalHoursDisplay =
									totalHoursMinutes > 0
										? m > 0
											? `${h}h${String(m).padStart(2, '0')}`
											: `${h}h`
										: '—';
								// Total HT (somme des unitPriceExclTax de chaque activité — valeur forfaitaire, pas horaire)
								const totalExclTax = activities.reduce(
									(sum, a) => sum + Number(a.unitPriceExclTax ?? 0),
									0
								);
								const totalExclTaxDisplay =
									activities.length > 0
										? new Intl.NumberFormat('fr-FR', {
												style: 'currency',
												currency: 'EUR',
											}).format(totalExclTax)
										: '—';
								return [
									{
										columnKey: 'durationHours' as const,
										value: totalHoursDisplay,
									},
									{
										columnKey: 'unitPriceExclTax' as const,
										value: totalExclTaxDisplay,
									},
								] satisfies FooterTotal<ActivityData>[];
							})()}
						/>
						<div className="flex items-center gap-2 mb-2">
							<label className="text-sm text-gray-600 flex flex-col items-start">
								<span className="sr-only">Date début</span>
								<input
									type="date"
									value={activityDateFilter.beginDateMin || ''}
									onChange={(e) =>
										setActivityDateFilter((prev) => ({
											...prev,
											beginDateMin: e.target.value,
										}))
									}
									className="w-32 text-sm border-b border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent"
								/>
							</label>

							<span className="text-gray-400 text-xl select-none">↕</span>

							<label className="text-sm text-gray-600 flex flex-col items-start">
								<span className="sr-only">Date fin</span>
								<input
									type="date"
									value={activityDateFilter.beginDateMax || ''}
									onChange={(e) =>
										setActivityDateFilter((prev) => ({
											...prev,
											beginDateMax: e.target.value,
										}))
									}
									className="w-32 text-sm border-b border-gray-300 focus:border-indigo-500 focus:outline-none bg-transparent"
								/>
							</label>
						</div>
					</CardSection>

					{/* TABLEAU DES ÉLÉMENTS LOGISTIQUES À FACTURER */}
					<CardSection icon={faClipboardList} title="Éléments logistiques">
						<RelatedItemsManager<LogisticElementData>
							linkedItems={formData.logisticElements || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, logisticElements: items }))
							}
							fetchAll={() =>
								logisticElementsApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => ('data' in res ? res.data : res))
							}
							isLinked={(le) => !!le.customerInvoice?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'date', label: 'Date' },
								{ key: 'label', label: 'Libellé' },
								{ key: 'product', label: 'Produit' },
								{ key: 'service', label: 'Service' },
								{
									key: 'unitPriceExclTax',
									label: 'Tarif HT',
									render: (_value: unknown, item: LogisticElementData) =>
										item.unitPriceExclTax != null
											? new Intl.NumberFormat('fr-FR', {
													style: 'currency',
													currency: 'EUR',
												}).format(Number(item.unitPriceExclTax))
											: '—',
								},
								{ key: 'quantity', label: 'Quantité' },
								{
									key: 'project',
									label: 'Affaire',
									render: (_value: unknown, item: LogisticElementData) => {
										if (!item.project?.id) return '—';
										return (
											<a
												href={`/app/projects/${item.project.id}/edit`}
												className="text-gray-600 hover:text-blue-600 hover:underline cursor-pointer"
												onClick={(e) => e.stopPropagation()}
											>
												{item.project.referenceNumber ?? `#${item.project.id}`}
											</a>
										);
									},
								},
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/logisticElements"
							linkedFooterTotals={(() => {
								const totalHT = (formData.logisticElements || []).reduce(
									(sum, le) =>
										sum + (le.unitPriceExclTax ?? 0) * (le.quantity ?? 0),
									0
								);
								return [
									{
										columnKey: 'unitPriceExclTax' as const,
										value:
											totalHT > 0
												? new Intl.NumberFormat('fr-FR', {
														style: 'currency',
														currency: 'EUR',
													}).format(totalHT)
												: '—',
									},
								] satisfies FooterTotal<LogisticElementData>[];
							})()}
						/>
					</CardSection>
				</div>

				{/* 2 COLONNES */}
				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					{/* TABLEAU DES ACOMPTES A INCLURE DANS LA FACTURE */}
					<CardSection icon={faMoneyBills} title="Acomptes">
						<RelatedItemsManager<AdvanceData>
							linkedItems={formData.advances || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, advances: items }))
							}
							fetchAll={() =>
								advancesApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => ('data' in res ? res.data : res))
							}
							isLinked={(le) => !!le.customerInvoice?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'referenceNumber', label: 'Référence' },
								{ key: 'amount', label: 'Montant' },
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/advances"
							linkedFooterTotals={(() => {
								const totalHT = (formData.advances || []).reduce(
									(sum, a) => sum + (a.amount ?? 0),
									0
								);
								return [
									{
										columnKey: 'amount' as const,
										value:
											totalHT > 0
												? new Intl.NumberFormat('fr-FR', {
														style: 'currency',
														currency: 'EUR',
													}).format(totalHT)
												: '—',
									},
								] satisfies FooterTotal<AdvanceData>[];
							})()}
						/>
					</CardSection>

					{/* TABLEAU DES AVOIRS À INCLURE DANS LA FACTURE */}
					<CardSection icon={faMoneyBillTransfer} title="Avoirs">
						<RelatedItemsManager<CreditNoteData>
							linkedItems={formData.creditNotes || []}
							setLinkedItems={(items) =>
								setFormData((f) => ({ ...f, creditNotes: items }))
							}
							fetchAll={() =>
								creditNotesApi
									.listPaginated(token, { limit: 999999 })
									.then((res) => ('data' in res ? res.data : res))
							}
							isLinked={(a) => !!a.customerInvoice?.id}
							columns={[
								{ key: 'id', label: 'ID' },
								{ key: 'referenceNumber', label: 'Référence' },
							]}
							linkedTitle="Liés"
							unlinkedTitle="Disponibles"
							idUrlPrefix="/app/creditNotes"
						/>
					</CardSection>
				</div>
			</form>

			<FormSaveActions
				isDirty={isDirty}
				isSubmitting={isSubmitting}
				onCancel={() => navigate('/app/customerInvoices')}
				onSave={() => handleSave(false)}
				onSaveAndLeave={() => handleSave(true)}
			/>
		</div>
	);
};

export default CustomerInvoiceForm;
